import React, { createContext, useContext, useEffect, useState } from "react";
import { api, tokenStore } from "../lib/api";
import { setUserContext as sentrySetUser } from "../lib/sentry";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mirror user identity into Sentry whenever it changes. No-op when
  // Sentry is disabled (DSN blank), so this costs nothing on free dev
  // builds. We only pass `id` + `role`; email/phone never leave the
  // client per the redaction contract in lib/sentry.js.
  useEffect(() => {
    sentrySetUser(user);
  }, [user]);

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        tokenStore.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokenStore.set(data.token);
    // Authoritative refresh from /auth/me so we always have the full current user (e.g. is_account_customer)
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data.user);
      return data.user;
    }
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // Phase B production-hardening: the server returns one of two shapes:
    //   1. `{ email_verified: false, email, message }` — production flow.
    //      No token, no session. Caller redirects to /verify-email.
    //   2. `{ token, user }` — legacy / test-mode flow (returned only
    //      when TEST_MODE_AUTO_VERIFY=1 server-side; never in real prod).
    if (data && data.token && data.user) {
      tokenStore.set(data.token);
      setUser(data.user);
      return { kind: "verified", user: data.user };
    }
    return {
      kind: "verification_required",
      email: data?.email || payload.email,
      message: data?.message,
    };
  };

  const logout = async () => {
    // Best-effort server-side cleanup for drivers — flips is_online=false
    // and clears any outstanding offer so dispatch can re-rotate. If the
    // driver is mid-trip the backend returns 409 and we surface a clear
    // message to the user without clearing the token, so they can either
    // finish the trip or ask an admin to force-release. For passengers
    // / admins this endpoint is a no-op 403 which we swallow.
    if (user?.role === "driver") {
      try {
        await api.post("/driver/sign-out");
      } catch (e) {
        if (e?.response?.status === 409) {
          // Block the logout — show the backend's reason via an alert
          // so the driver knows exactly what's wrong.
          const detail = e.response.data?.detail || {};
          const msg = detail.message || "Can't sign out — you're on an active trip.";
          // eslint-disable-next-line no-alert
          alert(msg);
          return { blocked: true, reason: detail.reason || "active_trip" };
        }
        // Network / 5xx / other — proceed with client-side sign-out anyway.
      }
    }
    tokenStore.clear();
    setUser(null);
    // Belt-and-braces: drop any axios default Authorization header that may
    // have been set during this session. Prevents the next mount from
    // briefly reusing the previous driver's token before the interceptor
    // runs against an empty tokenStore.
    try {
      // eslint-disable-next-line no-undef
      delete api.defaults.headers.common.Authorization;
    } catch {
      // No-op intentionally. Deleting an absent object property cannot throw
      // in practice; the guard exists for paranoid older browsers. If it
      // ever did fail there's nothing meaningful we can do — the user is
      // already mid-logout and we're about to redirect anyway.
    }
    return { blocked: false };
  };

  const refresh = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  /**
   * Adopt a session originating outside the normal login flow — currently
   * used by /verify-email when a fresh JWT is returned after the user
   * clicks their email verification link. Persists the token and seeds
   * the user object so the next route guard sees an authenticated session
   * without a redundant /auth/me round trip.
   */
  const adoptSession = async (token, userPayload) => {
    if (token) tokenStore.set(token);
    if (userPayload) setUser(userPayload);
    return userPayload;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
